import React, { useState } from 'react'
import { Alert, Button, Input } from './ui'
import { organizationsAPI } from '../api/organizations'
import type { Organization } from '../api/organizations'

interface Props {
  organization: Organization
  isOwner: boolean
  /** Called after a successful update so the parent can refresh org data. */
  onUpdated: (org: Organization) => void
}

const CONTACT_EMAIL = 'hello@catalyzeup.ai'

/**
 * Settings panel for an org's custom domain (e.g. happiness.wehappers.org).
 *
 * v1 behavior:
 *  - Show the current domain (or "Not set") to anyone who can see the org.
 *  - Owner can edit / clear via the dedicated PUT endpoint.
 *  - We deliberately don't expose self-serve DNS verification or Azure binding
 *    yet — those are operator tasks. The "Want self-serve? Contact us!" callout
 *    sets that expectation for any non-Chip customer poking around.
 */
export const CustomDomainSection: React.FC<Props> = ({ organization, isOwner, onUpdated }) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(organization.custom_domain ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentDomain = organization.custom_domain ?? null

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const trimmed = value.trim()
      const updated = await organizationsAPI.setCustomDomain(
        organization.id,
        trimmed === '' ? null : trimmed,
      )
      onUpdated(updated)
      setEditing(false)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Failed to save custom domain')
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!confirm('Remove the custom domain from this organization?')) return
    setSaving(true)
    setError(null)
    try {
      const updated = await organizationsAPI.setCustomDomain(organization.id, null)
      onUpdated(updated)
      setValue('')
      setEditing(false)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Failed to clear custom domain')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-lg font-semibold text-foreground mb-4">Custom Domain</h3>

        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}

        {!editing && (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-foreground-muted">Current domain</div>
              <div className="text-lg font-medium text-foreground">
                {currentDomain ? (
                  <a
                    href={`https://${currentDomain}`}
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {currentDomain}
                  </a>
                ) : (
                  <span className="text-foreground-muted">Not set</span>
                )}
              </div>
            </div>
            {isOwner && (
              <Button variant="ghost" onClick={() => setEditing(true)}>
                {currentDomain ? 'Change' : 'Set domain'}
              </Button>
            )}
          </div>
        )}

        {editing && isOwner && (
          <div className="space-y-3">
            <Input
              label="Custom domain"
              placeholder="happiness.wehappers.org"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2">
              <Button onClick={handleSave} loading={saving}>
                Save
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditing(false)
                  setValue(currentDomain ?? '')
                  setError(null)
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              {currentDomain && (
                <Button
                  variant="ghost"
                  onClick={handleClear}
                  disabled={saving}
                  className="text-error"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </section>

      {currentDomain && (
        <section className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">DNS setup</h3>
          <div className="space-y-3 text-sm text-foreground">
            <p>
              Your visitors will reach this organization at{' '}
              <strong>{currentDomain}</strong>. To finish wiring it up, point a
              CNAME record at the Sondely host and confirm with us so we can
              issue an SSL certificate for it.
            </p>
            <ol className="list-decimal pl-5 space-y-1 text-foreground-muted">
              <li>
                Create a CNAME record for <code className="text-foreground">{currentDomain}</code>{' '}
                pointing to the Sondely production host (we'll send you the
                exact target).
              </li>
              <li>Disable any CDN proxying (DNS-only) so the certificate can be validated.</li>
              <li>Email us once it's published; cert provisioning takes a few minutes.</li>
            </ol>
          </div>
        </section>
      )}

      <Alert variant="info">
        <div className="space-y-1">
          <div className="font-medium">Want self-serve custom domains?</div>
          <div>
            Today this is a guided setup. Email{' '}
            <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>{' '}
            and we'll bring your domain online.
          </div>
        </div>
      </Alert>
    </div>
  )
}
